import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CODIGO_DIGITOS, type CanalCodigo, type CodigoPedido } from '@kodica/shared';

import { Boton, Campo, Entrada, ErrorCaja } from '../componentes/ui';
import { ErrorApi, pedir } from '../lib/api';

/**
 * Recuperar la contraseña con un código al teléfono.
 *
 * ## Dos pasos, y el primero no dice nada
 *
 * Se pide el teléfono y **siempre** se contesta lo mismo: «si está registrado,
 * te llega un código». No es una respuesta perezosa — decir «ese número no
 * existe» convertiría esta pantalla en una forma de averiguar quién vive en el
 * fraccionamiento probando números.
 *
 * Eso tiene un costo real: quien se equivoque al teclear su propio número se
 * queda esperando un mensaje que no va a llegar. Por eso el segundo paso enseña
 * el número al que se mandó, para que lo relea antes de esperar de más.
 */
export function RecuperarPagina() {
  const navegar = useNavigate();
  const [paso, setPaso] = useState<'telefono' | 'codigo' | 'listo'>('telefono');
  const [telefono, setTelefono] = useState('');
  const [codigo, setCodigo] = useState('');
  const [password, setPassword] = useState('');
  const [aviso, setAviso] = useState('');
  /** Por dónde salió el último. Decide si tiene sentido ofrecer el respaldo. */
  const [canal, setCanal] = useState<CanalCodigo>('SMS');
  const [reenviado, setReenviado] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);

  /**
   * Pide el código. Sin canal, sale por el que tenga puesto el fraccionamiento;
   * con canal, es alguien a quien no le llegó el primero.
   */
  async function pedirCodigo(e: React.FormEvent | null, forzar?: CanalCodigo) {
    e?.preventDefault();
    setError(null);
    setOcupado(true);
    try {
      const r = await pedir<CodigoPedido>('/auth/recuperar', {
        metodo: 'POST',
        cuerpo: { telefono, ...(forzar ? { canal: forzar } : {}) },
      });
      setAviso(r.mensaje);
      setCanal(r.canal);
      if (forzar) setReenviado(true);
      setPaso('codigo');
    } catch (e) {
      setError(e instanceof ErrorApi ? e.message : 'No se pudo pedir el código');
    } finally {
      setOcupado(false);
    }
  }

  async function confirmar(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setOcupado(true);
    try {
      await pedir('/auth/recuperar/confirmar', {
        metodo: 'POST',
        cuerpo: { telefono, codigo, passwordNueva: password },
        // Un 401 aquí hablaría del código, no de una sesión que no existe.
        noCierraSesion: true,
      });
      setPaso('listo');
    } catch (e) {
      setError(e instanceof ErrorApi ? e.message : 'No se pudo cambiar la contraseña');
    } finally {
      setOcupado(false);
    }
  }

  return (
    <div className="mx-auto flex min-h-full max-w-md flex-col justify-center gap-6 p-6">
      <h1 className="text-headline-md text-on-surface">Recuperar tu contraseña</h1>

      {paso === 'telefono' && (
        <form onSubmit={pedirCodigo} className="flex flex-col gap-4">
          <Campo etiqueta="Tu teléfono" ayuda="El que tienes registrado en el fraccionamiento">
            <Entrada
              required
              type="tel"
              autoFocus
              value={telefono}
              onChange={(e) => setTelefono(e.target.value)}
            />
          </Campo>

          {error && <ErrorCaja>{error}</ErrorCaja>}

          <Boton variante="primario" type="submit" disabled={ocupado} className="w-full">
            {ocupado ? 'Mandando…' : 'Mandarme un código'}
          </Boton>
          <Boton variante="texto" type="button" onClick={() => navegar('/login')}>
            Volver
          </Boton>
        </form>
      )}

      {paso === 'codigo' && (
        <form onSubmit={confirmar} className="flex flex-col gap-4">
          <p className="text-body-md text-on-surface-variant">{aviso}</p>
          {/* El número, para que lo relea antes de esperar un mensaje que nunca
              va a llegar por un dígito de más. */}
          <p className="text-label-sm text-outline">
            Se mandó a {telefono} por {canal === 'WHATSAPP' ? 'WhatsApp' : 'mensaje de texto'}
          </p>

          <Campo etiqueta={`Código de ${CODIGO_DIGITOS} dígitos`}>
            <Entrada
              required
              autoFocus
              inputMode="numeric"
              maxLength={CODIGO_DIGITOS}
              value={codigo}
              onChange={(e) => setCodigo(e.target.value.replace(/\D/g, ''))}
            />
          </Campo>

          <Campo etiqueta="Tu contraseña nueva" ayuda="Al menos 8 caracteres">
            <Entrada
              required
              type="password"
              minLength={8}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </Campo>

          {error && <ErrorCaja>{error}</ErrorCaja>}

          <Boton variante="primario" type="submit" disabled={ocupado} className="w-full">
            {ocupado ? 'Cambiando…' : 'Cambiar mi contraseña'}
          </Boton>
          {/*
            **El respaldo lo dispara la persona, no el servidor.**

            Twilio contesta «aceptado» a un WhatsApp enseguida; que el
            destinatario no lo tenga se sabe después y por separado. Un cambio
            automático creería que salió y dejaría a alguien esperando.

            Quien mira el teléfono sabe si llegó. Y solo se paga el segundo
            mensaje cuando de verdad hizo falta.
          */}
          {canal === 'WHATSAPP' && !reenviado && (
            <Boton
              variante="contorno"
              type="button"
              disabled={ocupado}
              onClick={() => void pedirCodigo(null, 'SMS')}
            >
              No me llegó — mándamelo por mensaje de texto
            </Boton>
          )}

          {reenviado && (
            <p className="text-label-sm text-outline">
              Se mandó de nuevo. <strong>Usa el último que te llegue</strong>: el anterior ya no
              sirve.
            </p>
          )}

          <Boton variante="texto" type="button" onClick={() => setPaso('telefono')}>
            Usar otro teléfono
          </Boton>
        </form>
      )}

      {paso === 'listo' && (
        <div className="flex flex-col gap-4">
          <p className="text-body-md text-on-surface">
            Listo. Ya puedes entrar con tu contraseña nueva.
          </p>
          {/* Se dice, porque si no parece que la app falló en el otro aparato. */}
          <p className="text-body-md text-on-surface-variant">
            Por seguridad se cerró la sesión en los demás aparatos donde hubieras entrado.
          </p>
          <Boton variante="primario" onClick={() => navegar('/login')} className="w-full">
            Entrar
          </Boton>
        </div>
      )}
    </div>
  );
}
